import React, { useState, useEffect } from 'react';
import { Share2, Facebook, Twitter, Linkedin, MessageCircle, Mail, ExternalLink, Copy, Check } from 'lucide-react';
import { linksAPI } from '../services/api';
import { Button, Alert, LoadingSpinner } from './common';

const SocialSharing = ({ shortCode, linkTitle = '', linkDescription = '' }) => {
  const [shareUrls, setShareUrls] = useState(null);
  const [shareStats, setShareStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (shortCode) {
      loadShareUrls();
      loadShareStats();
    }
  }, [shortCode]);

  const loadShareUrls = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await linksAPI.getSocialShareUrls(shortCode, linkTitle, linkDescription);
      
      if (response.success) {
        setShareUrls(response.data);
      } else {
        setError(response.error || 'Failed to load share URLs');
      }
    } catch (error) {
      console.error('Error loading share URLs:', error);
      setError('Failed to load share URLs');
    } finally {
      setLoading(false);
    }
  };

  const loadShareStats = async () => {
    try {
      const response = await linksAPI.getSocialShareStats(shortCode);
      if (response.success) {
        setShareStats(response.data);
      }
    } catch (error) {
      console.error('Error loading share stats:', error);
    }
  };

  const handleShare = async (platform, url) => {
    try {
      // Track the share
      await linksAPI.trackSocialShare(shortCode, platform);
      
      // Open share URL in new window
      window.open(url, '_blank', 'width=600,height=400');
      
      // Reload stats
      loadShareStats();
    } catch (error) {
      console.error('Error sharing:', error);
    }
  };

  const copyShareUrl = async (url) => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      console.error('Error copying URL:', error);
      setError('Failed to copy URL');
    }
  };

  const socialPlatforms = [
    {
      name: 'Facebook',
      icon: Facebook,
      color: 'bg-blue-600 hover:bg-blue-700',
      urlKey: 'facebook'
    },
    {
      name: 'Twitter',
      icon: Twitter,
      color: 'bg-sky-500 hover:bg-sky-600',
      urlKey: 'twitter'
    },
    {
      name: 'LinkedIn',
      icon: Linkedin,
      color: 'bg-blue-700 hover:bg-blue-800',
      urlKey: 'linkedin'
    },
    {
      name: 'WhatsApp',
      icon: MessageCircle,
      color: 'bg-green-500 hover:bg-green-600',
      urlKey: 'whatsapp'
    },
    {
      name: 'Email',
      icon: Mail,
      color: 'bg-gray-600 hover:bg-gray-700',
      urlKey: 'email'
    },
    {
      name: 'Telegram',
      icon: ExternalLink,
      color: 'bg-blue-500 hover:bg-blue-600',
      urlKey: 'telegram'
    }
  ];

  if (!shortCode) {
    return (
      <div className="text-center py-10 text-ink-500">
        <Share2 className="w-12 h-12 text-ink-400 mx-auto mb-4" />
        <p>No link selected for social sharing</p>
      </div>
    );
  }

  return (
    <div>
      {error && (
        <Alert type="error" message={error} onClose={() => setError(null)} />
      )}

      {loading ? (
        <div className="flex flex-col items-center justify-center h-32">
          <LoadingSpinner size="lg" />
          <p className="text-ink-500 mt-4 text-sm">Loading share options...</p>
        </div>
      ) : shareUrls ? (
        <>
          {/* Share Statistics */}
          {shareStats && (
            <div className="mb-6 p-4 bg-gradient-to-r from-purple-50 to-pink-50 border border-purple-200 rounded-lg">
              <h4 className="text-sm font-medium text-purple-800 mb-2">Share Statistics</h4>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-purple-600">Total Shares:</span>
                  <span className="ml-2 font-semibold text-purple-800">{shareStats.totalShares || 0}</span>
                </div>
                <div>
                  <span className="text-purple-600">Platforms Tracked:</span>
                  <span className="ml-2 font-semibold text-purple-800">{Object.keys(shareStats.platformBreakdown || {}).length}</span>
                </div>
              </div>
              {shareStats.platformBreakdown && (
                <div className="mt-3 grid grid-cols-2 md:grid-cols-3 gap-2">
                  {Object.entries(shareStats.platformBreakdown).map(([platform, count]) => (
                    <div key={platform} className="flex items-center justify-between bg-white border border-purple-100 rounded px-2 py-1 text-xs">
                      <span className="capitalize text-purple-700">{platform}</span>
                      <span className="font-semibold text-purple-900">{count}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Social Platform Buttons */}
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-6">
            {socialPlatforms.map((platform) => {
              const Icon = platform.icon;
              const shareUrl = shareUrls.shareUrls?.[platform.urlKey];
              
              return (
                <Button
                  key={platform.name}
                  onClick={() => shareUrl && handleShare(platform.urlKey, shareUrl)}
                  variant="primary"
                  size="sm"
                  icon={Icon}
                  disabled={!shareUrl}
                  className={`${platform.color} text-white shadow-md hover:shadow-lg transition-all duration-200 ${
                    !shareUrl ? 'opacity-50 cursor-not-allowed' : ''
                  }`}
                >
                  {platform.name}
                </Button>
              );
            })}
          </div>

          {/* Direct Link Sharing */}
          <div className="mb-6">
            <h4 className="text-sm font-medium text-ink-700 mb-3">Direct Link</h4>
            <div className="flex items-center space-x-2">
              <input
                type="text"
                value={shareUrls.link?.shortUrl || ''}
                readOnly
                className="flex-1 px-3 py-2 border border-ink-300 rounded-lg bg-ink-50 text-sm font-mono"
              />
              <Button
                onClick={() => copyShareUrl(shareUrls.link?.shortUrl || '')}
                variant="outline"
                size="sm"
                icon={copied ? Check : Copy}
                className={`border-ink-300 text-ink-700 hover:bg-ink-50 transition-all duration-200 ${
                  copied ? 'border-green-300 text-green-700 bg-green-50' : ''
                }`}
              >
                {copied ? 'Copied!' : 'Copy'}
              </Button>
            </div>
          </div>

          {/* Link Information */}
          <div className="p-4 bg-ink-50 border border-ink-200 rounded-lg">
            <h4 className="text-sm font-medium text-ink-700 mb-2">Link Information</h4>
            <div className="space-y-2 text-sm">
              <div>
                <span className="text-ink-600">Title:</span>
                <span className="ml-2 text-ink-900">{shareUrls.link?.title || 'No title'}</span>
              </div>
              <div>
                <span className="text-ink-600">Description:</span>
                <span className="ml-2 text-ink-900">{shareUrls.link?.description || 'No description'}</span>
              </div>
              <div>
                <span className="text-ink-600">Short Code:</span>
                <span className="ml-2 font-mono text-ink-900">{shortCode}</span>
              </div>
            </div>
          </div>
        </>
      ) : (
        <div className="text-center text-ink-500">
          <Share2 className="w-16 h-16 mx-auto mb-2 text-ink-300" />
          <p>No share options available</p>
        </div>
      )}

    </div>
  );
};

export default SocialSharing;

